import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Intercepts navigation/exit attempts when there are unsaved changes.
 * Caller passes an `isDirty` flag; the hook returns a `guard(action)` wrapper
 * that either runs the action immediately (clean) or queues it behind a
 * confirmation modal (dirty). It also installs a `beforeunload` warning so
 * the browser prompts on tab close/refresh.
 */
export function useUnsavedChangesGuard(isDirty: boolean) {
  const [showDiscardModal, setShowDiscardModal] = useState(false)
  const pendingActionRef = useRef<(() => void) | null>(null)
  // One-shot flag: when the user has already explicitly confirmed leaving
  // (e.g. followed a canvas link through the discard modal), suppress the
  // browser's native `beforeunload` prompt for the next unload so they're
  // not asked twice.
  const bypassUnloadRef = useRef(false)

  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      if (bypassUnloadRef.current) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  // Call right before an intentional navigation away to skip the native
  // beforeunload confirmation once.
  const allowNextUnload = useCallback(() => { bypassUnloadRef.current = true }, [])

  const guard = useCallback(
    (action: () => void) => {
      if (isDirty) {
        pendingActionRef.current = action
        setShowDiscardModal(true)
      } else {
        action()
      }
    },
    [isDirty]
  )

  const confirmDiscard = useCallback(() => {
    setShowDiscardModal(false)
    const action = pendingActionRef.current
    pendingActionRef.current = null
    if (action) action()
  }, [])

  const cancelDiscard = useCallback(() => {
    setShowDiscardModal(false)
    pendingActionRef.current = null
  }, [])

  return { guard, allowNextUnload, showDiscardModal, confirmDiscard, cancelDiscard }
}
